import { C } from "../constants/colors";
import { WASTE_STREAMS, LEGACY_WASTE_STREAMS, STREAM_COLORS, MONTH_ABBR, wasteMetricOf, wasteMetricUnitLabel } from "../constants/wasteData";
import { sum, fmtMoney, fmtMetric, pct } from "../utils/format";
import { trailing12Months, last12MonthsRecords, dataAnchorDate, tonsOf } from "../utils/wasteAnalytics";
import { Card, SectionTag } from "../components/ui";
import { MultiLineChart } from "../components/charts";

const COMPLIANCE_STYLE={
  pass:{label:"Pass",color:C.green,bg:C.greenLight},
  review:{label:"Review",color:C.orange,bg:C.orangeLight},
  unknown:{label:"Unknown",color:C.muted,bg:C.sand},
};

export default function Dashboard({orgProfile,waste,setScreen}){
  const {org,hasAudit,diversionRateNow,resolvedMaterials,actionPlan,hasPlan}=orgProfile;
  const {wasteData,wasteDataConfig}=waste;
  const hasWasteData=wasteData.length>0;

  // ── Fully empty state ───────────────────────────────────────
  if(!hasWasteData&&!hasAudit){
    return (
      <div>
        <SectionTag label="Dashboard"/>
        <h2 style={{fontSize:22,fontWeight:800,color:C.navy,margin:"10px 0 4px"}}>{org.name}</h2>
        <Card style={{textAlign:"center",padding:"56px 24px",marginTop:16,border:`2px dashed ${C.border}`}}>
          <div style={{fontSize:36,marginBottom:14}}>📊</div>
          <div style={{fontWeight:700,color:C.navy,fontSize:18,marginBottom:10}}>Your dashboard will show up here</div>
          <p style={{color:C.muted,fontSize:14,lineHeight:1.7,maxWidth:480,margin:"0 auto 24px"}}>
            Once your organization has a completed waste audit and some hauling invoice or historical cost data on file,
            this page will show cost trends, diversion rates, compliance status, and location-by-location spend — all in
            one place.
          </p>
          <a href="mailto:?subject=Waste%20Free%20Tahoe%20Setup&body=Hi%2C%20could%20you%20help%20finish%20setting%20up%20our%20waste%20audit%20account%3F"
            style={{display:"inline-block",padding:"10px 22px",borderRadius:8,background:C.navy,color:C.white,fontWeight:600,fontSize:14,textDecoration:"none"}}>
            ✉ Contact Your Administrator
          </a>
        </Card>
      </div>
    );
  }

  // ── Cost aggregates (trailing 12 months) ────────────────────
  // Anchor to the latest date in the data so historical datasets display correctly.
  const anchor=dataAnchorDate(wasteData);
  const months=trailing12Months(anchor);
  const records12mo=last12MonthsRecords(wasteData,anchor);
  const totalAnnualSpend=sum(records12mo.map(r=>r.costDollars||0));

  const costByStream={};
  records12mo.forEach(r=>{ if(r.costDollars!=null) costByStream[r.stream]=(costByStream[r.stream]||0)+r.costDollars; });
  const topStreamEntry=Object.entries(costByStream).sort((a,b)=>b[1]-a[1])[0]||null;

  const lastMonth=months[months.length-1], prevMonth=months[months.length-2];
  const lastMonthCost=sum(records12mo.filter(r=>r.date===lastMonth).map(r=>r.costDollars||0));
  const prevMonthCost=sum(records12mo.filter(r=>r.date===prevMonth).map(r=>r.costDollars||0));
  const momChange=prevMonthCost>0?((lastMonthCost-prevMonthCost)/prevMonthCost)*100:null;

  const totalTons=sum(records12mo.map(r=>tonsOf(r)||0));
  const hasWeightData=records12mo.some(r=>tonsOf(r)!=null);
  const costPerTon=hasWeightData&&totalTons>0?totalAnnualSpend/totalTons:null;

  const row1Cards=[
    {label:"TOTAL ANNUAL SPEND",value:hasWasteData?fmtMoney(totalAnnualSpend):"—",color:C.navy},
    {label:"TOP STREAM BY COST",value:hasWasteData&&topStreamEntry?topStreamEntry[0]:"—",
      sub:hasWasteData&&topStreamEntry?fmtMoney(topStreamEntry[1]):null,color:C.teal},
    {label:"MONTH OVER MONTH",value:hasWasteData&&momChange!=null?`${momChange>0?"+":""}${momChange.toFixed(1)}%`:"—",
      color:!hasWasteData||momChange==null?C.muted:momChange>0?C.red:C.green,
      sub:hasWasteData&&momChange!=null?(momChange>0?"↑ vs prior month":momChange<0?"↓ vs prior month":"No change"):null},
  ];
  if(hasWeightData) row1Cards.push({label:"COST PER TON",value:costPerTon!=null?`$${costPerTon.toLocaleString(undefined,{maximumFractionDigits:0})}/ton`:"—",color:C.navy});

  // ── Diversion rate from invoice data ────────────────────────
  // Use volume (cu yd) when available, fall back to cost dollars.
  // MSW = landfill; all other streams = diverted.
  const hasVolumeData=records12mo.some(r=>r.volume!=null&&r.volume>0);
  const totalVol=hasVolumeData?sum(records12mo.map(r=>r.volume||0)):0;
  const mswVol=hasVolumeData?sum(records12mo.filter(r=>r.stream==="MSW").map(r=>r.volume||0)):0;
  const divertedVol=hasVolumeData?sum(records12mo.filter(r=>r.stream!=="MSW").map(r=>r.volume||0)):0;
  let invoiceDiversionRate=null;
  let invoiceDiversionSource=null;
  if(hasWasteData){
    if(hasVolumeData&&totalVol>0){
      invoiceDiversionRate=parseFloat(pct(divertedVol,totalVol)); invoiceDiversionSource="vol";
    }
    if(invoiceDiversionRate==null&&totalAnnualSpend>0){
      const divertedCost=sum(records12mo.filter(r=>r.stream!=="MSW").map(r=>r.costDollars||0));
      invoiceDiversionRate=parseFloat(pct(divertedCost,totalAnnualSpend)); invoiceDiversionSource="cost";
    }
  }

  // ── Diversion & compliance ───────────────────────────────────
  const landfillTons=sum(records12mo.filter(r=>r.stream==="MSW").map(r=>tonsOf(r)||0));

  // AB 1826: businesses generating 2+ tons/week of solid waste must arrange organics service.
  const weeklyTotalTons=totalTons/52;
  const hasCompostStream=records12mo.some(r=>r.stream==="Compost"&&((r.costDollars||0)>0||tonsOf(r)!=null));
  let ab1826Status="unknown";
  if(records12mo.length>0){
    if(weeklyTotalTons>=2) ab1826Status=hasCompostStream?"pass":"review";
    else if(weeklyTotalTons>0) ab1826Status="pass"; // below threshold — not required
  }

  // SB 1383: 75% organics diversion, estimated from the audit's characterized organics
  // (found vs. already-diverted per the resolved hauler/baseline data).
  const organicMaterials=resolvedMaterials.filter(m=>m.divertible==="compost"&&m.weight>0);
  const organicsFound=sum(organicMaterials.map(m=>m.weight));
  const organicsDiverted=sum(organicMaterials.filter(m=>m.resolved==="feasible_now").map(m=>m.weight));
  let sb1383Status="unknown";
  if(organicsFound>0) sb1383Status=(organicsDiverted/organicsFound)>=0.75?"pass":"review";

  const complianceOverall=(ab1826Status==="review"||sb1383Status==="review")?"review"
    :(ab1826Status==="unknown"||sb1383Status==="unknown")?"unknown":"pass";

  // ── Monthly spend trend by stream ────────────────────────────
  // Derived from the data itself (not filtered from the canonical list) so records
  // still using a pre-expansion stream name (e.g. old "Recycling"/"Hazardous") keep
  // showing up here instead of silently vanishing from the chart.
  const streamsWithCost=new Set(records12mo.filter(r=>(r.costDollars||0)!==0).map(r=>r.stream));
  const knownStreamOrder=[...WASTE_STREAMS,...LEGACY_WASTE_STREAMS];
  const streamsPresent=[
    ...knownStreamOrder.filter(s=>streamsWithCost.has(s)),
    ...[...streamsWithCost].filter(s=>!knownStreamOrder.includes(s)).sort(),
  ];
  const trendSeries=streamsPresent.map(s=>({
    key:s,label:s,color:STREAM_COLORS[s]||C.muted,
    data:months.map(m=>({
      label:MONTH_ABBR[Number(m.slice(5,7))-1],
      value:sum(records12mo.filter(r=>r.date===m&&r.stream===s).map(r=>r.costDollars||0)),
    })),
  }));

  // ── Location breakdown ───────────────────────────────────────
  const wasteMetricUnit=wasteMetricUnitLabel(wasteDataConfig);
  const byLocation={};
  records12mo.forEach(r=>{
    if(!byLocation[r.locationName]) byLocation[r.locationName]={cost:0,metric:0};
    byLocation[r.locationName].cost+=r.costDollars||0;
    byLocation[r.locationName].metric+=wasteMetricOf(r,wasteDataConfig)||0;
  });
  const locationRows=Object.entries(byLocation)
    .map(([name,v])=>({name,...v,pctOfTotal:totalAnnualSpend>0?(v.cost/totalAnnualSpend*100):0}))
    .sort((a,b)=>b.cost-a.cost);

  return (
    <div>
      <div style={{marginBottom:24}}>
        <SectionTag label="Dashboard"/>
        <h2 style={{fontSize:22,fontWeight:800,color:C.navy,margin:"10px 0 4px"}}>{org.name}</h2>
        <p style={{color:C.muted,fontSize:13}}>{org.industry} · {org.size} · {months[0]} → {months[months.length-1]}</p>
      </div>

      {/* Row 1 — cost summary */}
      <div style={{marginBottom:20}}>
        <div style={{opacity:hasWasteData?1:0.45,pointerEvents:hasWasteData?"auto":"none",
          display:"grid",gridTemplateColumns:`repeat(${row1Cards.length},1fr)`,gap:12}}>
          {row1Cards.map(k=>(
            <Card key={k.label} style={{padding:16}}>
              <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:6}}>{k.label}</div>
              <div style={{fontSize:20,fontWeight:800,color:k.color}}>{k.value}</div>
              {k.sub&&<div style={{fontSize:11,color:C.muted,marginTop:2}}>{k.sub}</div>}
            </Card>
          ))}
        </div>
        {!hasWasteData&&(
          <Card style={{marginTop:10,background:C.sand,border:`1px dashed ${C.border}`}}>
            <p style={{fontSize:12,color:C.muted,margin:0}}>
              Cost data needs an uploaded invoice or historical CSV.{" "}
              <button onClick={()=>setScreen("waste-hub")} style={{color:C.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600,fontSize:12}}>Go to Waste Data Hub →</button>
            </p>
          </Card>
        )}
      </div>

      {/* Row 2 — diversion & compliance */}
      <div style={{marginBottom:20}}>
        <div style={{opacity:(hasAudit||hasWasteData)?1:0.45,pointerEvents:(hasAudit||hasWasteData)?"auto":"none",
          display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:12}}>
          <Card style={{padding:16}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:6}}>DIVERSION RATE</div>
            {invoiceDiversionRate!=null?(
              <>
                <div style={{fontSize:20,fontWeight:800,color:C.green}}>{invoiceDiversionRate.toFixed(1)}%</div>
                <div style={{fontSize:10,color:C.muted,marginTop:3}}>
                  {invoiceDiversionSource==="vol"?"by volume · trailing 12 mo":"by cost · trailing 12 mo"}
                </div>
              </>
            ):hasAudit?(
              <>
                <div style={{fontSize:20,fontWeight:800,color:C.green}}>{diversionRateNow}%</div>
                <div style={{fontSize:10,color:C.muted,marginTop:3}}>from audit data</div>
              </>
            ):(
              <div style={{fontSize:20,fontWeight:800,color:C.muted}}>—</div>
            )}
          </Card>
          <Card style={{padding:16}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:6}}>MSW VOLUME</div>
            {hasVolumeData?(
              <>
                <div style={{fontSize:20,fontWeight:800,color:C.navy}}>{mswVol.toLocaleString(undefined,{maximumFractionDigits:0})} cu yd</div>
                <div style={{fontSize:10,color:C.muted,marginTop:3}}>landfill · trailing 12 mo</div>
              </>
            ):hasWeightData?(
              <>
                <div style={{fontSize:20,fontWeight:800,color:C.navy}}>{landfillTons>0?`${landfillTons.toFixed(1)} tons`:"—"}</div>
                <div style={{fontSize:10,color:C.muted,marginTop:3}}>landfill · trailing 12 mo</div>
              </>
            ):(
              <div style={{fontSize:20,fontWeight:800,color:C.muted}}>—</div>
            )}
          </Card>
          <Card style={{padding:16}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:6}}>DIVERTED VOLUME</div>
            {hasVolumeData?(
              <>
                <div style={{fontSize:20,fontWeight:800,color:C.green}}>{divertedVol.toLocaleString(undefined,{maximumFractionDigits:0})} cu yd</div>
                <div style={{fontSize:10,color:C.muted,marginTop:3}}>recycling + organics · trailing 12 mo</div>
              </>
            ):(
              <div style={{fontSize:20,fontWeight:800,color:C.muted}}>—</div>
            )}
          </Card>
          <Card style={{padding:16}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:6}}>COMPLIANCE STATUS</div>
            <div style={{display:"inline-block",fontSize:13,fontWeight:800,color:COMPLIANCE_STYLE[complianceOverall].color,background:COMPLIANCE_STYLE[complianceOverall].bg,padding:"3px 10px",borderRadius:10,marginBottom:6}}>
              {COMPLIANCE_STYLE[complianceOverall].label}
            </div>
            <div style={{fontSize:10,color:C.muted,lineHeight:1.6}}>
              AB 1826 (organics service): {COMPLIANCE_STYLE[ab1826Status].label}<br/>
              SB 1383 (75% diversion): {COMPLIANCE_STYLE[sb1383Status].label}
            </div>
          </Card>
          <Card style={{padding:16}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:6}}>EST. ANNUAL SAVINGS</div>
            {hasPlan&&actionPlan.estimated_annual_savings?(
              <div style={{fontSize:20,fontWeight:800,color:C.green}}>{fmtMoney(actionPlan.estimated_annual_savings)}</div>
            ):(
              <>
                <div style={{fontSize:12,color:C.muted,marginBottom:8,lineHeight:1.5}}>Run an audit to estimate savings</div>
                <button onClick={()=>setScreen(hasAudit?"results":"audit-prep")} style={{fontSize:12,color:C.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600}}>
                  {hasAudit?"Generate plan →":"Start audit →"}
                </button>
              </>
            )}
          </Card>
        </div>
        {!hasAudit&&!hasWasteData&&(
          <Card style={{marginTop:10,background:C.sand,border:`1px dashed ${C.border}`}}>
            <p style={{fontSize:12,color:C.muted,margin:0}}>
              Diversion and compliance need invoice data or a completed waste audit.{" "}
              <button onClick={()=>setScreen("audit-prep")} style={{color:C.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600,fontSize:12}}>Start an audit →</button>
            </p>
          </Card>
        )}
      </div>

      {/* Row 3 + 4 — trend chart & location breakdown, need wasteData */}
      {hasWasteData?(
        <>
          <Card style={{marginBottom:16}}>
            <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:2}}>Monthly Spend Trend — by Stream</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:8}}>Trailing 12 months</div>
            <MultiLineChart series={trendSeries}/>
          </Card>

          <Card style={{marginBottom:20}}>
            <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:14}}>Location Breakdown — Trailing 12 Months</div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:`2px solid ${C.border}`}}>
                    <th style={{textAlign:"left",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>LOCATION</th>
                    <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>ANNUAL SPEND</th>
                    <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>{wasteMetricUnit?wasteMetricUnit.toUpperCase():"VOLUME/WEIGHT"}</th>
                    <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>% OF TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {locationRows.map((l,i)=>(
                    <tr key={l.name} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.sand}}>
                      <td style={{padding:"8px 10px",fontWeight:600,color:C.navy}}>{l.name}</td>
                      <td style={{padding:"8px 10px",textAlign:"right",color:C.text}}>{fmtMoney(l.cost)}</td>
                      <td style={{padding:"8px 10px",textAlign:"right",color:C.text}}>{wasteMetricUnit?fmtMetric(l.metric,wasteMetricUnit):"—"}</td>
                      <td style={{padding:"8px 10px",textAlign:"right",color:C.muted}}>{l.pctOfTotal.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ):(
        <Card style={{textAlign:"center",padding:"32px 24px",border:`2px dashed ${C.border}`}}>
          <p style={{fontSize:13,color:C.muted,margin:0}}>
            Upload an invoice or historical data to see spend trends and location breakdowns.{" "}
            <button onClick={()=>setScreen("waste-hub")} style={{color:C.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600,fontSize:13}}>Go to Waste Data Hub →</button>
          </p>
        </Card>
      )}
    </div>
  );
}
